import {
    MeasurementRecordReadDto,
    MeasurementSource,
    MeasurementType,
} from '../_generated/api/tagged-hardware'
import { nullableDate } from './ApiLifeCycleModel'
import ApiModel from './ApiModel'
import Vector2 from './Vector2'

export default class Measurement extends ApiModel<MeasurementRecordReadDto> {
    get id() {
        return this.data.id ?? ''
    }

    get source() {
        return this.data.source ?? MeasurementSource.None
    }

    get isManual() {
        return this.source === MeasurementSource.Manual
    }

    get type() {
        return this.data.type ?? MeasurementType.None
    }

    get calibrationId() {
        return this.data.calibrationId ?? ''
    }

    get sensorId() {
        return this.data.sensorId ?? ''
    }

    get deviceId() {
        return this.data.deviceId ?? ''
    }

    get reading() {
        return this.data.readingData
    }

    get settings() {
        return this.reading?.settings
    }

    get normalized() {
        return this.data.normalized
    }

    get polarity() {
        return this.settings?.polarity ? 'positive' : 'negative'
    }

    get measuredBy() {
        return this.data.measuredBy ?? ''
    }

    get comment() {
        return this.data.overwritableComment ?? this.data.comment ?? ''
    }

    get archivedBy() {
        return this.data.archivedBy ?? ''
    }

    get archivedAt() {
        return nullableDate(this.data.archivedAt)
    }

    get isArchived() {
        return !!this.archivedAt
    }

    get measuredAt() {
        const { measuredAt } = this.data
        if (!measuredAt) return undefined
        return new Date(measuredAt)
    }

    /**
     * Get the trigger point of this measurement as an array of vectors.
     * The trigger point is represented as a line in the same coordinate system as the wave form.
     * The first index is the end of the line, which must exist for the trigger to be valid.
     * The second index is the start of the line if it exists.
     * Since the end point of the line is always required for a valid trigger,
     *  it is added as the first index to make implementing code more concise.
     */
    get trigger() {
        const graph = this.normalized
        if (!graph?.triggerEnd) return undefined
        const end = Vector2.from(graph.triggerEnd)
        if (!graph?.triggerStart) return [end]
        const start = Vector2.from(graph.triggerStart)
        return [end, start]
    }

    get coordinates() {
        const graph = this.normalized
        return Vector2.list(graph?.waveformGraph ?? [])
    }
}
